import { isDefined } from 'ionic-angular/util/util';
import { BaseObjectFilters } from '../store/bootstrap';
import { AclFilter } from '../store/queries/acl-filter';
import { PageFilter } from '../store/queries/page-filter';
import { VasState } from '../store/store-type';
import { AccessGroup } from './access-group/access-group.model';
import { AccessGroupFilters } from './access-group/access-group.reducer';
import { AccountObjectAclFilter } from './account-object/account-object.acl-filter';
import { AccountObjectQueryFactory } from './account-object/account-object.query-factory';
import { AccountAclFilter } from './account-object/account.acl-filter';
import { AccountStatistics } from './account-statistics/account-statistics.model';
import { Account } from './account/account.model';
import { BaseObjectQueryFactory } from './base-object/base-object.query-factory';
import { CategoryGroup } from './category-group/category-group.model';
import { CategoryAclFilter } from './category/category.acl-filter';
import { CategoryPageFilter } from './category/category.page-filter';
import { CategoryQueryFactory } from './category/category.query-factory';
import { ContactType } from './contact-type/contact-type.model';
import { ContactTypeFilters } from './contact-type/contact-type.reducer';
import { Contact } from './contact/contact.model';
import { ContactPageFilter } from './contact/contact.page-filter';
import { ContactFilters } from './contact/contact.reducer';
import { ControlType } from './control-type/control-type.model';
import { Control } from './control/control.model';
import { DefaultTask } from './default-task/default-task.model';
import { IDefaultTaskFilters } from './default-task/default-task.reducer';
import { Field } from './field/field.model';
import { File } from './file/file.model';
import { Form } from './form/form.model';
import { FormQueryFactory } from './form/form.queries';
import { Group } from './group/group.model';
import { JobStatusSummary } from './job-status-summary-item/job-status-summary.model';
import { JobStatus } from './job-status/job-status.model';
import { JobType } from './job-type/job-type.model';
import { JobAssigneeRestrictedAccountObjectAclFilterCustomCheck } from './job/job-assignee.restricted-account-object-acl-filter-custom-check';
import { Job } from './job/job.model';
import { JobPageFilter } from './job/job.page-filter';
import { JobQueryFactory } from './job/job.queries';
import { LookupType } from './lookup-type/lookup-type.model';
import { Lookup } from './lookup/lookup.model';
import { LookupPageFilter } from './lookup/lookup.page-filter';
import { Membership } from './membership/membership.model';
import { MembershipQueryFactory } from './membership/membership.queries';
import { Note } from './note/note.model';
import { NotificationTemplate } from './notification-template/notification-template.model';
import { QueryFactoryHelpers } from './query-factory-helpers';
import { ReportLayout } from './report-layout/report-layout.model';
import { JobDataAclFilter } from './report/job-data-acl.filter';
import { Report } from './report/report.model';
import {
    RestrictedAccountObjectAclFilter,
    RestrictedAccountObjectAclFilterCategoryCheck
} from './restricted-account-object/restricted-account-object.acl-filter';
import { RestrictedAccountObjectQueryFactory } from './restricted-account-object/restricted-account-object.query-factory';
import { SyncUpItem } from './sync-up-item/sync-up-item.model';
import { TaskType } from './task-type/task-type.model';
import { TaskQueryFactory } from './task/task-query-factory';
import { TaskAclFilterCategoryCheck } from './task/task.acl-filter';
import { Task } from './task/task.model';
import { TaskPageFilter } from './task/task.page-filter';
import { Transition } from './transition/transition.model';
import { User } from './user/user.model';
import { UserPageFilter } from './user/user.page-filter';
import { UserQueryFactory } from './user/user.queries';

export declare type listSelector<T> = (state: VasState) => Array<T>;
export declare type dictionarySelector<T> = (state: VasState) => { [id: string]: T };
export declare type parameterisedListSelector<T> = (...args) => listSelector<T>;
export declare type itemSelector<T> = (state: VasState) => T;
export declare type parameterisedItemSelector<T> = (...args) => itemSelector<T>;

export interface IState<T, F> {
    ids: Array<string>;
    entities: { [id: string]: T };
    selected?: string;
    filters?: F;
}

export declare type AccessGroupQueryFactory = CategoryQueryFactory<AccessGroup, AccessGroupFilters>;
export declare type AccountQueryFactory = BaseObjectQueryFactory<Account, BaseObjectFilters>;
export declare type AccountStatisticsQueryFactory = AccountObjectQueryFactory<AccountStatistics, BaseObjectFilters>;
export declare type CategoryGroupQueryFactory = AccountObjectQueryFactory<CategoryGroup, BaseObjectFilters>;
export declare type ContactQueryFactory = RestrictedAccountObjectQueryFactory<Contact, ContactFilters>;
export declare type ContactTypeQueryFactory = CategoryQueryFactory<ContactType, ContactTypeFilters>;
export declare type ControlQueryFactory = RestrictedAccountObjectQueryFactory<Control, BaseObjectFilters>;
export declare type ControlTypeQueryFactory = BaseObjectQueryFactory<ControlType, BaseObjectFilters>;
export declare type DefaultTaskQueryFactory = AccountObjectQueryFactory<DefaultTask, IDefaultTaskFilters>;
export declare type FieldQueryFactory = AccountObjectQueryFactory<Field, BaseObjectFilters>;
export declare type FileQueryFactory = AccountObjectQueryFactory<File, BaseObjectFilters>;
export declare type GroupQueryFactory = RestrictedAccountObjectQueryFactory<Group, BaseObjectFilters>;
export declare type JobStatusQueryFactory = CategoryQueryFactory<JobStatus, BaseObjectFilters>;
export declare type JobStatusSummaryQueryFactory = AccountObjectQueryFactory<JobStatusSummary, BaseObjectFilters>;
export declare type JobTypeQueryFactory = CategoryQueryFactory<JobType, BaseObjectFilters>;
export declare type TaskTypeQueryFactory = CategoryQueryFactory<TaskType, BaseObjectFilters>;
export declare type LookupQueryFactory = RestrictedAccountObjectQueryFactory<Lookup, BaseObjectFilters>;
export declare type LookupTypeQueryFactory = CategoryQueryFactory<LookupType, BaseObjectFilters>;
export declare type NoteQueryFactory = RestrictedAccountObjectQueryFactory<Note, BaseObjectFilters>;
export declare type NotificationTemplateQueryFactory = AccountObjectQueryFactory<NotificationTemplate, BaseObjectFilters>;
export declare type ReportQueryFactory = AccountObjectQueryFactory<Report, BaseObjectFilters>;
export declare type ReportLayoutQueryFactory = RestrictedAccountObjectQueryFactory<ReportLayout, BaseObjectFilters>;
export declare type SyncUpItemQueryFactory = AccountObjectQueryFactory<SyncUpItem, BaseObjectFilters>;
export declare type TransitionQueryFactory = RestrictedAccountObjectQueryFactory<Transition, BaseObjectFilters>;

export class Qry {

    protected static instance: Qry;
    protected readonly accessGroupQueryFactory: AccessGroupQueryFactory;
    protected readonly accountQueryFactory: AccountQueryFactory;
    protected readonly accountStatisticsQueryFactory: AccountStatisticsQueryFactory;
    protected readonly categoryGroupQueryFactory: CategoryGroupQueryFactory;
    protected readonly contactQueryFactory: ContactQueryFactory;
    protected readonly contactTypeQueryFactory: ContactTypeQueryFactory;
    protected readonly controlQueryFactory: ControlQueryFactory;
    protected readonly controlTypeQueryFactory: ControlTypeQueryFactory;
    protected readonly defaultTaskQueryFactory: DefaultTaskQueryFactory;
    protected readonly fieldQueryFactory: FieldQueryFactory;
    protected readonly fileQueryFactory: FileQueryFactory;
    protected readonly formQueryFactory: FormQueryFactory;
    protected readonly groupQueryFactory: GroupQueryFactory;
    protected readonly jobQueryFactory: JobQueryFactory;
    protected readonly jobStatusQueryFactory: JobStatusQueryFactory;
    protected readonly jobStatusSummaryQueryFactory: JobStatusSummaryQueryFactory;
    protected readonly taskQueryFactory: TaskQueryFactory;
    protected readonly jobTypeQueryFactory: JobTypeQueryFactory;
    protected readonly taskTypeQueryFactory: TaskTypeQueryFactory;
    protected readonly lookupQueryFactory: LookupQueryFactory;
    protected readonly lookupTypeQueryFactory: LookupTypeQueryFactory;
    protected readonly membershipQueryFactory: MembershipQueryFactory;
    protected readonly noteQueryFactory: NoteQueryFactory;
    protected readonly notificationTemplateQueryFactory: NotificationTemplateQueryFactory;
    protected readonly reportQueryFactory: ReportQueryFactory;
    protected readonly reportLayoutQueryFactory: ReportLayoutQueryFactory;
    protected readonly syncUpItemQueryFactory: SyncUpItemQueryFactory;
    protected readonly transitionQueryFactory: TransitionQueryFactory;
    protected readonly userQueryFactory: UserQueryFactory;

    protected readonly helperQueryFactory: QueryFactoryHelpers;

    static getInstance(): Qry {

        if (!isDefined(Qry.instance)) {
            Qry.instance = new Qry();
        }

        return Qry.instance;
    }

    constructor() {

        if (!isDefined(Qry.instance)) {
            Qry.instance = this;
        }

        this.accessGroupQueryFactory = new CategoryQueryFactory<AccessGroup, AccessGroupFilters>(
            'accessGroup',
            new CategoryPageFilter<AccessGroup, AccessGroupFilters>(),
            new CategoryAclFilter<AccessGroup>('accessGroups')
        );

        this.accountQueryFactory = new BaseObjectQueryFactory<Account, BaseObjectFilters>(
            'account',
            new PageFilter<Account, BaseObjectFilters>(),
            new AccountAclFilter()
        );

        this.accountStatisticsQueryFactory = new AccountObjectQueryFactory<AccountStatistics, BaseObjectFilters>(
            'accountStatistics',
            new PageFilter<AccountStatistics, BaseObjectFilters>(),
            new AccountObjectAclFilter<AccountStatistics>()
        );

        this.categoryGroupQueryFactory = new AccountObjectQueryFactory<CategoryGroup, BaseObjectFilters>(
            'categoryGroup',
            new PageFilter<CategoryGroup, BaseObjectFilters>(),
            new AccountObjectAclFilter<CategoryGroup>()
        );

        this.contactQueryFactory = new RestrictedAccountObjectQueryFactory<Contact, ContactFilters>(
            'contact',
            new ContactPageFilter(this),
            new RestrictedAccountObjectAclFilter<Contact>([
                new RestrictedAccountObjectAclFilterCategoryCheck<Contact>('contactType', 'contactTypes')
            ])
        );

        this.contactTypeQueryFactory = new CategoryQueryFactory<ContactType, ContactTypeFilters>(
            'contactType',
            new CategoryPageFilter<ContactType, ContactTypeFilters>(),
            new CategoryAclFilter<ContactType>('contactTypes')
        );

        this.controlQueryFactory = new RestrictedAccountObjectQueryFactory<Control, BaseObjectFilters>(
            'control',
            new PageFilter<Control, BaseObjectFilters>(),
            new RestrictedAccountObjectAclFilter<Control>()
        );

        this.controlTypeQueryFactory = new BaseObjectQueryFactory<ControlType, BaseObjectFilters>(
            'controlType',
            new PageFilter<ControlType, BaseObjectFilters>(),
            new AclFilter<ControlType>()
        );

        this.defaultTaskQueryFactory = new AccountObjectQueryFactory<DefaultTask, IDefaultTaskFilters>(
            'defaultTask',
            new PageFilter<DefaultTask, IDefaultTaskFilters>(),
            new AccountObjectAclFilter<DefaultTask>()
        );

        this.fieldQueryFactory = new AccountObjectQueryFactory<Field, BaseObjectFilters>(
            'field',
            new PageFilter<Field, BaseObjectFilters>(),
            new AccountObjectAclFilter<Field>()
        );

        this.formQueryFactory = new FormQueryFactory(
            'form',
            new PageFilter<Form, BaseObjectFilters>(),
            new RestrictedAccountObjectAclFilter<Form>()
        );

        this.groupQueryFactory = new RestrictedAccountObjectQueryFactory<Group, BaseObjectFilters>(
            'group',
            new PageFilter<Group, BaseObjectFilters>(),
            new RestrictedAccountObjectAclFilter<Group>()
        );

        this.jobQueryFactory = new JobQueryFactory(
            'job',
            new JobPageFilter(this),
            new RestrictedAccountObjectAclFilter<Job>([
                new RestrictedAccountObjectAclFilterCategoryCheck<Job>('jobStatus', 'jobStatuses'),
                new RestrictedAccountObjectAclFilterCategoryCheck<Job>('jobType', 'jobTypes'),
                new JobAssigneeRestrictedAccountObjectAclFilterCustomCheck()
            ])
        );

        this.jobStatusQueryFactory = new CategoryQueryFactory<JobStatus, BaseObjectFilters>(
            'jobStatus',
            new CategoryPageFilter<JobStatus, BaseObjectFilters>(),
            new CategoryAclFilter<JobStatus>('jobStatuses')
        );

        this.jobStatusSummaryQueryFactory = new AccountObjectQueryFactory<JobStatusSummary, BaseObjectFilters>(
            'jobStatusSummary',
            new PageFilter<JobStatusSummary, BaseObjectFilters>(),
            new AccountObjectAclFilter<JobStatusSummary>()
        );

        this.jobTypeQueryFactory = new CategoryQueryFactory<JobType, BaseObjectFilters>(
            'jobType',
            new CategoryPageFilter<JobType, BaseObjectFilters>(),
            new CategoryAclFilter<JobType>('jobTypes')
        );

        this.lookupQueryFactory = new RestrictedAccountObjectQueryFactory<Lookup, BaseObjectFilters>(
            'lookup',
            new LookupPageFilter(this),
            new RestrictedAccountObjectAclFilter<Lookup>([
                new RestrictedAccountObjectAclFilterCategoryCheck<Lookup>('lookupType', 'lookupTypes')
            ])
        );

        this.lookupTypeQueryFactory = new CategoryQueryFactory<LookupType, BaseObjectFilters>(
            'lookupType',
            new CategoryPageFilter<LookupType, BaseObjectFilters>(),
            new CategoryAclFilter<LookupType>('lookupTypes')
        );

        this.membershipQueryFactory = new MembershipQueryFactory(
            'membership',
            new PageFilter<Membership, BaseObjectFilters>(),
            new AccountObjectAclFilter<Membership>()
        );

        this.reportLayoutQueryFactory = new RestrictedAccountObjectQueryFactory<ReportLayout, BaseObjectFilters>(
            'reportLayout',
            new PageFilter<ReportLayout, BaseObjectFilters>(),
            new RestrictedAccountObjectAclFilter<ReportLayout>()
        );

        this.syncUpItemQueryFactory = new AccountObjectQueryFactory<SyncUpItem, BaseObjectFilters>(
            'syncUpItem',
            new PageFilter<SyncUpItem, BaseObjectFilters>(),
            new AccountObjectAclFilter<SyncUpItem>()
        );

        this.transitionQueryFactory = new RestrictedAccountObjectQueryFactory<Transition, BaseObjectFilters>(
            'transition',
            new PageFilter<Transition, BaseObjectFilters>(),
            new RestrictedAccountObjectAclFilter<Transition>()
        );

        this.userQueryFactory = new UserQueryFactory(
            'user',
            new UserPageFilter(this, this.membershipQueryFactory),
            new AclFilter<User>()
        );

        // depends on job

        this.fileQueryFactory = new AccountObjectQueryFactory<File, BaseObjectFilters>(
            'file',
            new PageFilter<File, BaseObjectFilters>(),
            new JobDataAclFilter<File>([], this.job)
        );

        this.noteQueryFactory = new RestrictedAccountObjectQueryFactory<Note, BaseObjectFilters>(
            'note',
            new PageFilter<Note, BaseObjectFilters>(),
            new RestrictedAccountObjectAclFilter<Note>()
        );

        this.notificationTemplateQueryFactory = new AccountObjectQueryFactory<NotificationTemplate, BaseObjectFilters>(
            'notificationTemplate',
            new PageFilter<NotificationTemplate, BaseObjectFilters>(),
            new AccountObjectAclFilter<NotificationTemplate>()
        );

        this.reportQueryFactory = new AccountObjectQueryFactory<Report, BaseObjectFilters>(
            'report',
            new PageFilter<Report, BaseObjectFilters>(),
            new JobDataAclFilter<Report>([], this.job)
        );

        this.taskQueryFactory = new TaskQueryFactory(
            'task',
            new TaskPageFilter(this),
            new JobDataAclFilter<Task>([
                new TaskAclFilterCategoryCheck('taskType', 'taskTypes')
            ], this.job)
        );

        this.taskTypeQueryFactory = new CategoryQueryFactory<TaskType, BaseObjectFilters>(
            'taskType',
            new CategoryPageFilter<TaskType, BaseObjectFilters>(),
            new CategoryAclFilter<TaskType>('taskTypes')
        );

        // last

        this.helperQueryFactory = new QueryFactoryHelpers(
            this.userQueryFactory,
            this.accountQueryFactory,
            this.membershipQueryFactory
        );
    }

    get accessGroup(): AccessGroupQueryFactory {
        return this.accessGroupQueryFactory;
    }

    get account(): AccountQueryFactory {
        return this.accountQueryFactory;
    }

    get accountStatistics(): AccountStatisticsQueryFactory {
        return this.accountStatisticsQueryFactory;
    }

    get categoryGroup(): CategoryGroupQueryFactory {
        return this.categoryGroupQueryFactory;
    }

    get contact(): ContactQueryFactory {
        return this.contactQueryFactory;
    }

    get contactType(): ContactTypeQueryFactory {
        return this.contactTypeQueryFactory;
    }

    get control(): ControlQueryFactory {
        return this.controlQueryFactory;
    }

    get controlType(): ControlTypeQueryFactory {
        return this.controlTypeQueryFactory;
    }

    get defaultTask(): DefaultTaskQueryFactory {
        return this.defaultTaskQueryFactory;
    }

    get field(): FieldQueryFactory {
        return this.fieldQueryFactory;
    }

    get file(): FileQueryFactory {
        return this.fileQueryFactory;
    }

    get form(): FormQueryFactory {
        return this.formQueryFactory;
    }

    get group(): GroupQueryFactory {
        return this.groupQueryFactory;
    }

    get helper(): QueryFactoryHelpers {
        return this.helperQueryFactory;
    }

    get job(): JobQueryFactory {
        return this.jobQueryFactory;
    }

    get jobStatus(): JobStatusQueryFactory {
        return this.jobStatusQueryFactory;
    }

    get jobStatusSummary(): JobStatusSummaryQueryFactory {
        return this.jobStatusSummaryQueryFactory;
    }

    get task(): TaskQueryFactory {
        return this.taskQueryFactory;
    }

    get taskType(): TaskTypeQueryFactory {
        return this.taskTypeQueryFactory;
    }

    get jobType(): JobTypeQueryFactory {
        return this.jobTypeQueryFactory;
    }

    get lookup(): LookupQueryFactory {
        return this.lookupQueryFactory;
    }

    get lookupType(): LookupTypeQueryFactory {
        return this.lookupTypeQueryFactory;
    }

    get membership(): MembershipQueryFactory {
        return this.membershipQueryFactory;
    }

    get note(): NoteQueryFactory {
        return this.noteQueryFactory;
    }

    get notificationTemplate(): NotificationTemplateQueryFactory {
        return this.notificationTemplateQueryFactory;
    }

    get report(): ReportQueryFactory {
        return this.reportQueryFactory;
    }

    get reportLayout(): ReportLayoutQueryFactory {
        return this.reportLayoutQueryFactory;
    }

    get syncUpItem(): SyncUpItemQueryFactory {
        return this.syncUpItemQueryFactory;
    }

    get transition(): TransitionQueryFactory {
        return this.transitionQueryFactory;
    }

    get user(): UserQueryFactory {
        return this.userQueryFactory;
    }

}



// WEBPACK FOOTER //
// ./src/vas/business-objects/query-facade.ts